import { useState, useEffect, useRef, useContext } from 'react';
import AIEduButton from '../../components/Button/AIEduButton';
import AIEduSearch from '../../components/Search/AIEduSearch';
import AIEduDropDown from '../../components/DropDown/AIEduDropDown';
import AIEduSpinner from '../../components/Spinner/AIEduSpinner';
import AIEduDataTable from '../../components/DataTable/AIEduDataTable';
import AIEduPaginator from '../../components/Paginator/AIEduPaginator';
import AIEduTooltip from '../../components/Tooltip/AIEduTooltip';
import AIEduToast from '../../components/Toast/AIEduToast';
import AIEduInputTextFixedLabel from '../../components/Input/AIEduInputTextFixedLabel';
import AddUser from './AddUser';
import './user.css';
import { useAuthenticate } from '../../hooks';
import { API } from '../../services';
import { Context } from '../../context/Context';
import {
    setToastComponent,
    showErrorToast,
    showSuccessToast,
} from '../../services/setup/ApiResponseHandler';
import DeleteUserDialog from './DeleteUserDialog';
import { useIntl } from 'react-intl';
import {
    APPROVED,
    ASC,
    AUTO_APPROVED,
    DESC,
    MANUAL_APPROVED,
    PENDING,
    REJECTED,
    ROLE_ADMIN,
    ROLE_USER,
} from '../../utils/Constants';
import tickIcon from '../../assets/images/tick.svg';
import crossIcon from '../../assets/images/cross.svg';
import { formatDateWithTimeStamp } from '../../utils/Utils';
import PageTitle from '../../layout/PageTitle/pageTitle';

const User = () => {
    const intl = useIntl();
    const { formatMessage: f } = intl;
    const [users, setUsers] = useState([]);
    const [editingRows, setEditingRows] = useState({});
    const [tableKey, setTableKey] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [visible, setVisible] = useState(false);
    const [deleteAction, setDeleteAction] = useState(null);
    const toastRef = useRef(null);

    const [activeFilter, setActiveFilter] = useState(null);
    const [selectedUsersList, setSelectedUsersList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [adminCount, setAdminCount] = useState(0);
    const [deleteMessagesList, setDeleteMessagesList] = useState([]);
    const {
        refreshMap,
        isAddUserVisible,
        setIsAddUserVisible,
        sideMenuCurrentActive,
        setDefaultAccessType,
    } = useContext(Context);
    const [title, setTitle] = useState(false);

    const { accountInfo } = useAuthenticate();
    const { account, organizations, place } = accountInfo ?? {};
    const { placeId } = place ?? {};
    const { organizationId } = organizations?.[0] ?? {};
    const { userId: loginId } = account ?? {};

    const [sortField, setSortField] = useState('createdTime');
    const [sortOrder, setSortOrder] = useState(-1);

    const toggleFilter = (type) => {
        if (activeFilter === type) {
            setActiveFilter(null);
        } else {
            setActiveFilter(type);
        }
    };

    const selectionClear = () => {
        setEditingRows({});
        setTableKey(!tableKey);
        setSelectedUsersList([]);
    };

    const onPageChange = (e) => {
        const page = e.page;
        const size = e.rows;

        setFirst(e.first);
        setRows(size);
        setPage(page);
        selectionClear();

        getAllUsersList(page, size, sortField, sortOrder, searchTerm);
    };

    const getAllUsersList = (start, rowsPerPage, sortBy, sort, search) => {
        setLoading(true);
        const params = {
            start,
            rowsPerPage,
            role: activeFilter || 'ALL',
            sortBy: sortBy || 'createdTime',
            sort: sort === 1 ? ASC : DESC,
            search,
        };
        API.getUsersList(organizationId, placeId, params)
            .then(({ data }) => {
                const {
                    users: usersResponse,
                    total,
                    adminCount: count,
                    defaultRole,
                    sortBy,
                    sort,
                } = data ?? {};
                if (usersResponse?.length === 0 && start > 0 && total > 0) {
                    setFirst(0);
                    setPage(0);
                    getAllUsersList(0, rowsPerPage, sortBy, sortOrder, search);
                } else {
                    const usersList = [];
                    usersResponse?.forEach((user) => {
                        const {
                            userId,
                            email,
                            userName,
                            role,
                            nfcTagId,
                            userStatus,
                            createdTime,
                            isNewUserPresent,
                        } = user ?? {};
                        usersList.push({
                            userId,
                            email,
                            userName,
                            role,
                            nfcTagId,
                            userStatus: userStatus.toUpperCase(),
                            createdTime,
                            isNewUserPresent,
                        });
                    });
                    setUsers(usersList);
                    setAdminCount(count);
                    setTotalRecords(total);
                    setDefaultAccessType(defaultRole || ROLE_USER);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const resetPageParams = () => {
        setFirst(0);
        setSortField('createdTime');
        setSortOrder(-1);
        setSearchTerm('');
        selectionClear();
    };

    useEffect(() => {
        setToastComponent(toastRef.current);
    }, []);

    useEffect(() => {
        if (sideMenuCurrentActive !== 0) {
            setIsAddUserVisible(false);
        }

        if (activeFilter === null) {
            resetPageParams();
            setPage(0);
            getAllUsersList(0, rows);
        } else {
            setActiveFilter(null);
        }
    }, [refreshMap.user]);

    useEffect(() => {
        resetPageParams();
        setPage(0);
        getAllUsersList(0, rows);
    }, [activeFilter]);

    const handleDeleteUsersList = (userList) => {
        setDeleteAction(() => () => {
            API.deleteUsers(organizationId, placeId, userList)
                .then((res) => {
                    if (res.status === 200) {
                        setSelectedUsersList((prev) =>
                            prev.filter(
                                (user) => !userList.includes(user.userId)
                            )
                        );
                        if (userList === users) {
                            setPage(Math.max(0, page - 1));
                        }
                        getAllUsersList(
                            userList === users ? Math.max(0, page - 1) : page,
                            rows,
                            sortField,
                            sortOrder,
                            searchTerm
                        );
                    }
                })
                .finally(() => {
                    setVisible(false);
                    setDeleteMessagesList([]);
                });
        });
        setTitle('Delete');
        setVisible(true);
    };

    const navigateToAddUser = () => {
        setIsAddUserVisible(true);
    };

    const handleUserSearch = () => {
        setFirst(0);
        selectionClear();
        setPage(0);
        getAllUsersList(0, rows, sortField, sortOrder, searchTerm);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleUserSearch();
        }
    };

    const userAppprovalAPI = (rowData, status) => {
        const { userId, role, nfcTagId } = rowData ?? {};

        setDeleteAction(() => () => {
            API.updateUsers(organizationId, placeId, {
                userId,
                status,
                role,
                nfcTagId,
            })
                .then((response) => {
                    if (status === APPROVED) {
                        if (response.status === 200) {
                            getAllUsersList(
                                page,
                                rows,
                                sortField,
                                sortOrder,
                                searchTerm
                            );

                            showSuccessToast(
                                f({ id: 'AI_EDU_USER_APPROVED_SUCCESS' })
                            );
                        } else {
                            showErrorToast(
                                f({ id: 'AI_EDU_USER_APPROVED_FAILURE' })
                            );
                        }
                    } else {
                        if (response.status === 200) {
                            getAllUsersList(
                                page,
                                rows,
                                sortField,
                                sortOrder,
                                searchTerm
                            );

                            showSuccessToast(
                                f({ id: 'AI_EDU_USER_REJECTED_SUCCESS' })
                            );
                        } else {
                            showErrorToast(
                                f({ id: 'AI_EDU_USER_REJECTED_FAILURE' })
                            );
                        }
                    }
                })
                .finally(() => {
                    setVisible(false);
                    setDeleteMessagesList([]);
                });
        });
        let approvalMessage;
        if (status === APPROVED) {
            approvalMessage =
                'Are you sure you want to approve the pending user?';
        } else {
            approvalMessage =
                'Are you sure you want to reject the pending user?';
        }
        setDeleteMessagesList([approvalMessage]);
        setVisible(true);
        setTitle('Update');
    };

    const onRowEditInit = (rowData) => {
        // Mark the row as being edited
        setEditingRows((prev) => ({
            ...prev,
            [rowData.userId]: { ...rowData },
        }));

        setTableKey(!tableKey);
    };

    const onRowEditSave = (rowData) => {
        const { userId, role, nfcTagId } = rowData;

        if (role == ROLE_USER) {
            if (adminCount === 1) {
                onRowEditCancel(rowData);

                showErrorToast(
                    'Atleast one admin is required in an organization'
                );
                return;
            }
            if (userId === loginId) {
                setDeleteAction(() => () => {
                    API.updateUsers(organizationId, placeId, {
                        userId,
                        role,
                        nfcTagId,
                    })
                        .then((response) => {
                            if (response.status === 200) {
                                getAllUsersList(0, rows);
                            } else {
                                onRowEditCancel(rowData);
                                showErrorToast(
                                    f({ id: 'AI_EDU_ACCESS_TYPE_FAILURE' })
                                );
                            }
                        })
                        .finally(() => {
                            setVisible(false);
                            setDeleteMessagesList([]);
                        });
                });
                setDeleteMessagesList([
                    'You are changing your role from Admin to User.',
                    'You will be signed out. Are you Sure?',
                ]);
                setTitle('Update');
                setVisible(true);
                return;
            }
        }

        API.updateUsers(organizationId, placeId, {
            userId,
            role,
            nfcTagId,
        }).then((response) => {
            if (response.status === 200) {
                // Remove the row from editing state
                setEditingRows((prev) => {
                    const newEditingRows = { ...prev };
                    delete newEditingRows[userId];
                    return newEditingRows;
                });

                setTableKey(!tableKey);

                getAllUsersList(page, rows, sortField, sortOrder, searchTerm);
                showSuccessToast(f({ id: 'AI_EDU_ACCESS_TYPE_SUCCESS' }));
            } else {
                onRowEditCancel(rowData);
                showErrorToast(f({ id: 'AI_EDU_ACCESS_TYPE_FAILURE' }));
            }
        });
    };

    const onRowEditCancel = (rowData) => {
        // Revert to original data
        setUsers((prev) =>
            prev.map((user) =>
                user.userId === rowData.userId
                    ? editingRows[rowData.userId]
                    : user
            )
        );
        // Remove the row from editing state
        setEditingRows((prev) => {
            const newEditingRows = { ...prev };
            delete newEditingRows[rowData.userId];
            return newEditingRows;
        });

        setTableKey(!tableKey);
    };

    const isEditing = (rowData) => {
        return editingRows.hasOwnProperty(rowData.userId);
    };

    const accessTypeOptions = [
        { label: f({ id: 'AI_EDU_ADMIN' }), value: ROLE_ADMIN },
        { label: f({ id: 'AI_EDU_USER' }), value: ROLE_USER },
    ];

    const handleChange = (rowData, e) => {
        const { name, value } = e.target;
        setUsers((prev) =>
            prev.map((user) =>
                user.userId === rowData.userId
                    ? { ...user, [name]: value }
                    : user
            )
        );
    };

    const accessTypeTemplate = (rowData) => {
        return isEditing(rowData) || rowData.userStatus === PENDING ? (
            <AIEduDropDown
                name='role'
                className='user-row-access-type'
                value={rowData.role}
                options={accessTypeOptions}
                onChange={(e) => handleChange(rowData, e)}
                placeholder={f({ id: 'AI_EDU_SELECT_ACCESS_TYPE' })}
            />
        ) : rowData.role === ROLE_ADMIN ? (
            f({ id: 'AI_EDU_ADMIN' })
        ) : (
            f({ id: 'AI_EDU_USER' })
        );
    };

    const nfcTagTemplate = (rowData) => {
        return isEditing(rowData) || rowData.userStatus === PENDING ? (
            <AIEduInputTextFixedLabel
                type='text'
                name='nfcTagId'
                value={rowData?.nfcTagId}
                onChange={(e) => handleChange(rowData, e)}
                placeholder='Enter NFC Tag'
            />
        ) : !!rowData.nfcTagId ? (
            rowData.nfcTagId
        ) : (
            '--'
        );
    };

    const ApprovalTypeTagTemplate = ({ userStatus, isNewUserPresent }) => {
        let approvalTypeStyle = '';
        let approvalText = '';
        let userTagShow = false;
        switch (userStatus) {
            case AUTO_APPROVED:
                approvalTypeStyle = 'autoApprovalStyle';
                approvalText = 'Auto Approved';
                userTagShow = isNewUserPresent;
                break;
            case MANUAL_APPROVED:
                approvalTypeStyle = 'manualApprovalStyle';
                approvalText = 'Approved';
                userTagShow = isNewUserPresent;
                break;
            case PENDING:
                approvalTypeStyle = 'requestPendingStyle';
                approvalText = 'Request';
                userTagShow = true;
                break;
            case REJECTED:
                approvalTypeStyle = 'rejectedStyle';
                approvalText = 'Rejected';
                userTagShow = true;
                break;
            default:
                approvalTypeStyle = '';
                approvalText = userStatus;
                userTagShow = true;
                break;
        }
        return (
            userStatus &&
            userTagShow &&
            approvalTypeStyle && (
                <div className={`user_approval ${approvalTypeStyle}`}>
                    {approvalText}
                </div>
            )
        );
    };

    const createdTimeTemplate = (rowData) => {
        return !!rowData.createdTime
            ? formatDateWithTimeStamp(rowData.createdTime)
            : '--';
    };

    const emailTemplate = (rowData) => {
        const { email, userStatus, isNewUserPresent } = rowData ?? {};
        return (
            <div className='flex gap-3'>
                <div className='user-email-template' title={email}>
                    {email}
                </div>
                <div className='flex'>
                    <ApprovalTypeTagTemplate
                        userStatus={userStatus}
                        isNewUserPresent={isNewUserPresent}
                    />
                    {isNewUserPresent && (
                        <div className={`user_approval ml-2`}>New</div>
                    )}
                </div>
            </div>
        );
    };

    const rowEditorTemplate = (rowData) => {
        const { userId, role } = rowData ?? {};

        return isEditing(rowData) ? (
            <div className='flex align-items-center gap-3'>
                <AIEduButton
                    outlined
                    label={f({ id: 'AI_EDU_UPDATE' })}
                    className='btnSecondary updateBtn'
                    onClick={() => onRowEditSave(rowData)}
                />
                <div
                    className='action_background'
                    onClick={() => onRowEditCancel(rowData)}
                    style={{ paddingTop: '3px' }}
                >
                    <img src={crossIcon} />
                </div>
            </div>
        ) : rowData?.userStatus === PENDING ? (
            <div className='flex gap-3'>
                <AIEduTooltip target='.approve-user' />
                <div
                    className='action_background approve-user'
                    data-pr-tooltip='Approve'
                    data-pr-position='top'
                    onClick={() => userAppprovalAPI(rowData, APPROVED)}
                >
                    <img src={tickIcon} />
                </div>
                <AIEduTooltip target='.reject-user' />
                <div
                    className='action_background reject-user'
                    data-pr-tooltip='Reject'
                    data-pr-position='top'
                    onClick={() => userAppprovalAPI(rowData, REJECTED)}
                    style={{ paddingTop: '3px' }}
                >
                    <img src={crossIcon} />
                </div>
            </div>
        ) : rowData?.userStatus === REJECTED ? (
            <div>
                <i
                    className='pi pi-trash'
                    style={{ fontSize: '20px', cursor: 'pointer' }}
                    onClick={() => {
                        setDeleteMessagesList([
                            f({ id: 'AI_EDU_DELETE_USER_TEXT' }),
                        ]);
                        handleDeleteUsersList([userId]);
                    }}
                />
            </div>
        ) : (
            <div className='flex gap-3'>
                <i
                    className='pi pi-pen-to-square'
                    style={{ fontSize: '20px', cursor: 'pointer' }}
                    onClick={() => onRowEditInit(rowData)}
                />
                <i
                    className='pi pi-trash'
                    style={{ fontSize: '20px', cursor: 'pointer' }}
                    onClick={() => {
                        if (role === ROLE_ADMIN && adminCount === 1) {
                            setDeleteMessagesList([
                                f({ id: 'AI_EDU_DELETE_ORG_PROMPT' }),
                            ]);
                        } else {
                            setDeleteMessagesList([
                                f({ id: 'AI_EDU_DELETE_USER_TEXT' }),
                            ]);
                        }
                        handleDeleteUsersList([userId]);
                    }}
                />
            </div>
        );
    };

    const columns = [
        {
            field: 'email',
            header: 'User Email',
            minWidth: '25rem',
            width: '25%',
            body: emailTemplate,
            sortable: true,
        },
        {
            field: 'userName',
            header: f({ id: 'AI_EDU_USER_NAME' }),
            minWidth: '15rem',
            width: '25%',
            sortable: true,
        },
        {
            field: 'nfcTagId',
            header: f({ id: 'AI_EDU_NFC_TAG' }),
            body: nfcTagTemplate,
            minWidth: '15rem',
            width: '20%',
        },
        {
            field: 'role',
            header: f({ id: 'AI_EDU_ACCESS_TYPE' }),
            body: accessTypeTemplate,
            minWidth: '10rem',
            width: '10%',
            sortable: true,
        },
        {
            field: 'createdTime',
            header: 'Enrolled Date',
            body: createdTimeTemplate,
            minWidth: '10rem',
            width: '10%',
            sortable: true,
        },
        {
            header: f({ id: 'AI_EDU_ACTION' }),
            body: rowEditorTemplate,
            minWidth: '10rem',
        },
    ];

    const handleDeleteMultipleUsers = () => {
        const users = selectedUsersList?.map((user) => user.userId);
        const selectedAdminCount = selectedUsersList?.filter((user) =>
            user.userStatus !== PENDING &&
            user.userStatus !== REJECTED &&
            user.role === ROLE_ADMIN
                ? true
                : false
        ).length;

        if (selectedAdminCount === adminCount) {
            setDeleteMessagesList([
                f({
                    id: 'AI_EDU_DELETE_ORG_PROMPT',
                }),
            ]);
        } else {
            setDeleteMessagesList([
                f({
                    id: 'AI_EDU_DELETE_USER_TEXT',
                }),
            ]);
        }
        handleDeleteUsersList(users);
    };

    const isSelectable = (data) => {
        return data.userStatus === PENDING ? false : true;
    };
    const isRowSelectable = (e) => {
        return e.data ? isSelectable(e.data) : true;
    };

    const getRowClass = (rowData) => {
        return rowData.userStatus === PENDING ? 'pending-row' : '';
    };

    const onSort = (e) => {
        const { field, order } = { field: e.sortField, order: e.sortOrder };

        getAllUsersList(
            page,
            rows,
            field,
            order,
            !!searchTerm ? searchTerm : null
        );

        setSortField(field);
        setSortOrder(order);
    };

    const sortIcon = (options) => {
        if (options.sortOrder === 1) {
            return (
                <i
                    className='pi pi-sort-up-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
        if (options.sortOrder === -1) {
            return (
                <i
                    className='pi pi-sort-down-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
    };

    return (
        <>
            {loading && <AIEduSpinner />}
            <AIEduToast ref={toastRef} />
            <DeleteUserDialog
                visible={visible}
                hideDeleteUsersDialog={() => {
                    setVisible(!visible);
                    setDeleteMessagesList([]);
                }}
                title={title}
                deleteSelectedUsers={deleteAction}
                deleteMessagesList={deleteMessagesList}
            />
            {!isAddUserVisible ? (
                <div className='user'>
                    <PageTitle
                        title={f({ id: 'AI_EDU_USERS' })}
                        count={totalRecords}
                    />
                    <div className='mb-4 flex justify-content-between flex-wrap gap-3'>
                        <div className='flex flex-wrap gap-6'>
                            <AIEduSearch
                                value={searchTerm}
                                setValue={setSearchTerm}
                                placeholder={f({ id: 'AI_EDU_SEARCH_USER' })}
                                searchHandler={handleUserSearch}
                                handleInputKeyDown={handleInputKeyDown}
                            />
                            <div className='flex align-items-center gap-2'>
                                <div className='user-access-type-txt'>
                                    {f({ id: 'AI_EDU_ACCESS_TYPE' })}:
                                </div>
                                <AIEduButton
                                    label={f({ id: 'AI_EDU_ADMIN' })}
                                    onClick={() => toggleFilter(ROLE_ADMIN)}
                                    className={
                                        activeFilter === ROLE_ADMIN
                                            ? 'userBtnSecondary mr-2'
                                            : 'p-btn-filter mr-2'
                                    }
                                />
                                <AIEduButton
                                    label={f({ id: 'AI_EDU_USER' })}
                                    onClick={() => toggleFilter(ROLE_USER)}
                                    className={
                                        activeFilter === ROLE_USER
                                            ? 'userBtnSecondary'
                                            : 'p-btn-filter'
                                    }
                                />
                            </div>
                        </div>
                        <div className='flex align-items-center flex-wrap'>
                            <AIEduButton
                                icon='pi pi-trash'
                                className='users-delete'
                                onClick={handleDeleteMultipleUsers}
                                disabled={selectedUsersList.length === 0}
                            />
                            <div className='vertical_divider mx-3' />
                            <AIEduButton
                                label={f({ id: 'AI_EDU_ADD_USER' })}
                                icon='pi pi-plus'
                                className='btnPrimary'
                                onClick={navigateToAddUser}
                            />
                        </div>
                    </div>

                    <div
                        className='mb-2'
                        style={{
                            height: '1rem',
                            fontFamily: 'SamsungOne-700, sans-serif',
                        }}
                    >
                        {selectedUsersList?.length > 0 && (
                            <span>
                                {selectedUsersList.length} of {users?.length}{' '}
                                selected
                            </span>
                        )}
                    </div>

                    <AIEduDataTable
                        key={tableKey}
                        data={users}
                        columns={columns}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        sortIcon={sortIcon}
                        onSort={onSort}
                        lazy={true}
                        selection={selectedUsersList}
                        isDataSelectable={isRowSelectable}
                        rowClassName={getRowClass}
                        dataKey='userId'
                        onSelectionChange={(e) => setSelectedUsersList(e.value)}
                        scrollHeight='60vh'
                        selectionMode='multiple'
                        emptyMessage={f({ id: 'AI_EDU_NO_DATA_TO_SHOW' })}
                    />
                    {users?.length > 0 && (
                        <AIEduPaginator
                            rows={rows}
                            first={first}
                            totalRecords={totalRecords}
                            onPageChange={onPageChange}
                        />
                    )}
                </div>
            ) : (
                <AddUser
                    setIsAddUserVisible={setIsAddUserVisible}
                    accessTypeOptions={accessTypeOptions}
                    setLoading={setLoading}
                />
            )}
        </>
    );
};

export default User;
